import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MetricsPanel from "../components/MetricsPanel";
import TicketsPanel from "../components/TicketsPanel";
import SatisfactionStrip from "../components/SatisfactionStrip";
import { PeopleCard } from "./ProjectDetail";
import { formatDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

const SLA_ROLES = ["super_admin", "project_manager"];

export default function OrganizationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", contact_email: "", phone: "" });
  const [editErr, setEditErr] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const canEdit = user && user.role === "super_admin";
  const [ratings, setRatings] = useState(null);
  const isManager = ["super_admin", "project_manager"].includes(user.role);

  // SLA card state
  const [slaMode, setSlaMode] = useState(null); // "status" | "priority" | null
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [slaValues, setSlaValues] = useState({}); // key -> string (hours, or "" for blank)
  const [slaSaving, setSlaSaving] = useState(false);

  const canManageSla = user && SLA_ROLES.includes(user.role);

  useEffect(() => {
    setData(null);
    setErr(null);
    api
      .get(`/organizations/${id}`)
      .then((r) => setData(r.data))
      .catch((e) =>
        setErr(
          e.response?.status === 404
            ? "Not found or no access."
            : "Failed to load."
        )
      );
    setRatings(null);
    if (isManager) api.get("/reports/ratings", { params: { organization_id: id } }).then((r) => setRatings(r.data)).catch(() => setRatings({ error: true }));
  }, [id, isManager]);

  // Fetch SLA mode and relevant rows
  useEffect(() => {
    if (!canManageSla) return;
    api
      .get("/company/sla")
      .then((r) => {
        const mode = r.data.mode || "status";
        setSlaMode(mode);
        if (mode === "priority") {
          return api
            .get("/masters/priority")
            .then((pr) => setPriorities(pr.data))
            .catch(() => toast.error("Failed to load priorities"));
        } else {
          return api
            .get("/ticket-statuses")
            .then((sr) => setStatuses(sr.data))
            .catch(() => toast.error("Failed to load ticket statuses"));
        }
      })
      .catch(() => {
        // Fall back to status mode
        setSlaMode("status");
        api
          .get("/ticket-statuses")
          .then((r) => setStatuses(r.data))
          .catch(() => toast.error("Failed to load ticket statuses"));
      });
  }, [canManageSla]);

  // Seed SLA values once org data + rows are loaded
  useEffect(() => {
    if (!data || !canManageSla || slaMode == null) return;
    const overrides = data.organization.sla_overrides || {};
    const initial = {};
    if (slaMode === "priority") {
      priorities.forEach((p) => {
        initial[p.value] =
          overrides[p.value] != null ? String(overrides[p.value] / 60) : "";
      });
    } else {
      statuses.forEach((s) => {
        if (s.enabled) {
          initial[s.key] =
            overrides[s.key] != null ? String(overrides[s.key] / 60) : "";
        }
      });
    }
    setSlaValues(initial);
  }, [data, statuses, priorities, slaMode, canManageSla]);

  const handleSlaSave = async () => {
    setSlaSaving(true);
    try {
      const sla_overrides = {};
      if (slaMode === "priority") {
        priorities.forEach((p) => {
          const raw = slaValues[p.value];
          if (raw === "" || raw == null) return;
          const hours = parseFloat(raw);
          if (isFinite(hours) && hours > 0) {
            sla_overrides[p.value] = Math.round(hours * 60);
          }
        });
      } else {
        statuses
          .filter((s) => s.enabled)
          .forEach((s) => {
            const raw = slaValues[s.key];
            if (raw === "" || raw == null) return;
            const hours = parseFloat(raw);
            if (isFinite(hours) && hours > 0) {
              sla_overrides[s.key] = Math.round(hours * 60);
            }
          });
      }
      const { data: saved } = await api.put(`/organizations/${id}/sla`, { sla_overrides });
      // Update local org data with new overrides
      setData((prev) => ({
        ...prev,
        organization: { ...prev.organization, sla_overrides: saved.sla_overrides },
      }));
      // Reseed input values from response
      const newValues = {};
      if (slaMode === "priority") {
        priorities.forEach((p) => {
          newValues[p.value] =
            saved.sla_overrides[p.value] != null
              ? String(saved.sla_overrides[p.value] / 60)
              : "";
        });
      } else {
        statuses
          .filter((s) => s.enabled)
          .forEach((s) => {
            newValues[s.key] =
              saved.sla_overrides[s.key] != null
                ? String(saved.sla_overrides[s.key] / 60)
                : "";
          });
      }
      setSlaValues(newValues);
      toast.success("SLA overrides saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save SLA overrides");
    } finally {
      setSlaSaving(false);
    }
  };

  const openEdit = () => {
    const o = data.organization;
    setEditForm({ name: o.name || "", contact_email: o.contact_email || "", phone: o.phone || "" });
    setEditErr("");
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditErr("");
    setEditSaving(true);
    try {
      await api.patch(`/organizations/${id}`, {
        name: editForm.name,
        contact_email: editForm.contact_email,
        phone: editForm.phone,
      });
      const r = await api.get(`/organizations/${id}`);
      setData(r.data);
      setEditOpen(false);
      toast.success("Client updated");
    } catch (err) {
      setEditErr(formatApiError(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  if (err) return <div className="surface-card p-8 text-center text-slate-500">{err}</div>;
  if (!data) return <div className="surface-card p-8 text-center text-slate-400">Loading…</div>;

  const enabledStatuses = statuses.filter((s) => s.enabled);
  const slaRows =
    slaMode === "priority"
      ? priorities.map((p) => ({
          key: p.value,
          label: p.label,
          subLabel: p.value,
          defaultHint:
            p.sla_minutes != null
              ? `default ${Math.round((p.sla_minutes / 60) * 10) / 10}h`
              : "—",
          testId: `org-sla-${p.value}`,
        }))
      : enabledStatuses.map((s) => ({
          key: s.key,
          label: s.label,
          subLabel: s.key,
          defaultHint:
            s.sla_minutes != null
              ? `default ${s.sla_minutes / 60}h`
              : "—",
          testId: `org-sla-${s.key}`,
        }));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Client</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">{data.organization.name}</h1>
          <p className="text-sm text-slate-500 mt-1">{data.projects_count} project(s)</p>
          {(data.organization.contact_email || data.organization.phone) && (
            <p className="text-xs text-slate-400 mt-1">
              {data.organization.contact_email || "—"} · {data.organization.phone || "—"}
            </p>
          )}
        </div>
        {canEdit && (
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-300" onClick={openEdit} data-testid="org-edit-button">
                <PencilSimple size={14} className="mr-1.5" /> Edit details
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Edit client</DialogTitle></DialogHeader>
              <form onSubmit={submitEdit} className="space-y-4" data-testid="org-edit-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Organization name</Label>
                  <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="org-edit-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Contact email</Label>
                  <Input type="email" value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} data-testid="org-edit-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Phone</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="org-edit-phone-input" />
                </div>
                {editErr && <div className="text-xs text-rose-600">{editErr}</div>}
                <DialogFooter>
                  <Button type="submit" disabled={editSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="org-edit-submit">
                    {editSaving ? "Saving…" : "Save changes"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <MetricsPanel metrics={data.metrics} />
      {isManager && <SatisfactionStrip ratings={ratings} />}

      <div className="surface-card overflow-hidden" data-testid="org-projects-card">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Delivery</div>
          <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Projects ({(data.projects || []).length})</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {(data.projects || []).map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`}
              className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                <div className="text-xs text-slate-400 capitalize">{p.project_type ? String(p.project_type).replace(/_/g, " ") : "—"} · created {formatDate(p.created_at)}</div>
              </div>
              <div className="text-xs text-slate-500 whitespace-nowrap shrink-0">
                <span className="font-semibold text-slate-800 tabular-nums">{p.open_count}</span> open · <span className="tabular-nums">{p.ticket_count}</span> total
              </div>
            </Link>
          ))}
          {(data.projects || []).length === 0 && <div className="px-5 py-6 text-xs text-slate-400">No projects yet.</div>}
        </div>
      </div>

      <TicketsPanel scopeParams={{ organization_id: id }} testId="org-tickets-panel" />

      <PeopleCard members={data.members} linkNames={isManager}
        footnote="Super admins and project managers have access to every client." />

      {canManageSla && (
        <div className="surface-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-700">SLA</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Override the default SLA targets for this client. Leave blank to use the workspace default.
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {slaMode == null && (
              <div className="px-5 py-6 text-xs text-slate-400 text-center">Loading…</div>
            )}
            {slaMode != null && slaRows.length === 0 && (
              <div className="px-5 py-6 text-xs text-slate-400 text-center">
                {slaMode === "priority" ? "No priorities configured." : "No enabled statuses."}
              </div>
            )}
            {slaRows.map((row) => (
              <div key={row.key} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{row.label}</div>
                  <div className="text-xs text-slate-400">{row.subLabel}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder={row.defaultHint}
                    value={slaValues[row.key] ?? ""}
                    onChange={(e) =>
                      setSlaValues((prev) => ({ ...prev, [row.key]: e.target.value }))
                    }
                    data-testid={row.testId}
                    className="w-28 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                  <span className="text-xs text-slate-400">hrs</span>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-slate-100">
            <Button
              onClick={handleSlaSave}
              disabled={slaSaving}
              data-testid="org-sla-save"
            >
              {slaSaving ? "Saving…" : "Save SLA overrides"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
